// React.
import { useEffect, useMemo, useRef, useState } from 'react';

// Third-parties.
import { Column, usePagination, useSortBy, useTable } from 'react-table';
import TableCSVExport from '../../../component/TableCSVExport';

// Types.
import InstanceTarget from '../../../types/instance/InstanceTarget';

// Components.
import ReactTable from "../../ReactTable";

// Helper.
import { getDefaultPageSize } from '../../../helpers/utils';
import { TransactionContentionType } from "../../../types/instance/TransactionContentionType";
import ExpandableCell from "../../ExpandableCell";
import Helper from "../../../helpers/Helper";
import { useLocation, useNavigate } from "react-router-dom";
import SearchInput from '../../SearchInput';

export default function TransactionsContentionTable(props: {
    error: string | null,
    loading: boolean,
    data: TransactionContentionType[],
    instance: InstanceTarget
}) {
    const { error, loading, data, instance } = props;
    const inputRef = useRef<HTMLInputElement>(null);

    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    // CSV headers
    const headers: any[] = useMemo(() => {
        return [
            {
                label: 'Blocking Transaction',
                key: 'blockingtxn',
            },
            {
                label: 'Waiting Transaction',
                key: 'waitingtxn',
            },
            {
                label: 'Contention Duration',
                key: 'contentionduration',
            },
            {
                label: 'Blocking SQL Array',
                key: 'blockingsqlarray',
            },
            {
                label: 'Waiting SQL Array',
                key: 'waitingsqlarray',
            },
            {
                label: 'Database',
                key: 'database',
            },
            {
                label: 'Schema',
                key: 'schema',
            },
            {
                label: 'Table',
                key: 'table',
            },
            {
                label: 'Index',
                key: 'index',
            },
            {
                label: 'Blocking Txn Fprint',
                key: 'blockingtxnfingerprint',
            },
            {
                label: 'Waiting Txn Fprint',
                key: 'waitingtxnfingerprint',
            },
            {
                label: 'Waiting Stmt Fprint',
                key: 'waitingstmtfingerprint',
            },
        ]
    }, []);

    // Table columns
    const columns: Column[] = useMemo(() => {
        return [
            {
                Header: 'Blocking Transaction',
                accessor: 'blockingtxn',
                headerClassName: 'text',
                canSort: true,
                className: 'col-width-10 text-nowrap',
                sortDescFirst: false,
                sortType: 'basic'
            },
            {
                Header: 'Waiting Transaction',
                accessor: 'waitingtxn',
                headerClassName: 'text',
                canSort: true,
                className: 'col-width-10 text-nowrap',
                sortDescFirst: false,
                sortType: 'basic'
            },
            {
                Header: 'Contention Duration',
                accessor: 'contentionduration',
                headerClassName: 'text',
                canSort: true,
                sortDescFirst: false,
                className: 'col-width-5 text-nowrap',
                sortType: 'basic',
                Cell: ({ cell }: any) => Helper.getTimeInEnglish(cell.value)
            },
            {
                Header: 'Blocking SQL Array',
                accessor: 'blockingsqlarray',
                className: 'w-500',
                disableSortBy: true,
                canSort: false,
                Cell: ({ cell: { value } }) => <ExpandableCell data={value}/>,
            },
            {
                Header: 'Waiting SQL Array',
                accessor: 'waitingsqlarray',
                disableSortBy: true,
                className: 'w-500',
                canSort: false,
                Cell: ({ cell: { value } }) => <ExpandableCell data={value}/>,
            },
            {
                Header: 'Database',
                accessor: 'database',
                headerClassName: 'text',
                canSort: true,
                sortDescFirst: false,
                className: 'col-width-10 text-nowrap',
                sortType: 'basic'
            },
            {
                Header: 'Schema',
                accessor: 'schema',
                headerClassName: 'text',
                className: 'col-width-10 text-nowrap',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic'
            },
            {
                Header: 'Table',
                accessor: 'table',
                headerClassName: 'text',
                className: 'col-width-10 text-nowrap',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic'
            },
            {
                Header: 'Index',
                accessor: 'index',
                headerClassName: 'text',
                className: 'col-width-10 text-nowrap',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic'
            },
            {
                Header: 'Blocking Txn Fprint',
                accessor: 'blockingtxnfingerprint',
                headerClassName: 'text',
                className: 'col-width-10 text-nowrap',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic'
            },
            {
                Header: 'Waiting Txn Fprint',
                accessor: 'waitingtxnfingerprint',
                headerClassName: 'text',
                className: 'col-width-10 text-nowrap',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic'
            },
            {
                Header: 'Waiting Stmt Fprint',
                accessor: 'waitingstmtfingerprint',
                className: 'col-width-10 text-nowrap',
                headerClassName: 'text',
                canSort: true,
                sortDescFirst: false,
                sortType: 'basic'
            },
        ]
    }, [instance.id, data]);

    const tableData = useMemo(() => {
        // Filter data based on tableFilter
        // const seen = new Set<string>();
        // const uniqueData = filteredData.filter(item => {
        //     if (seen.has(item.blockingtxn)) {
        //         return false;
        //     }
        //     seen.add(item.blockingtxn);
        //     return true;
        // });

        return data.filter((row) =>
            row.blockingtxn.toLowerCase().includes(tableFilter.toLowerCase()) ||
            row.waitingtxn.toLowerCase().includes(tableFilter.toLowerCase())
        );
    }, [data, tableFilter]);


    // Transform function to format arrays for CSV export
    const transformCsvData = useMemo(() => (csvData: any[]) => {
        return csvData.map((item) => {
            // Format blockingsqlarray and waitingsqlarray for CSV
            const blockingsqlarray = Array.isArray(item.blockingsqlarray)
                ? item.blockingsqlarray.map((obj: any) =>
                    `sqlhash: ${obj.sqlhash}, sqltext: ${obj.sqltext.replace(/"/g, '""')}`
                ).join('\n')
                : '';

            const waitingsqlarray = Array.isArray(item.waitingsqlarray)
                ? item.waitingsqlarray.map((obj: any) =>
                    `sqlhash: ${obj.sqlhash}, sqltext: ${obj.sqltext.replace(/"/g, '""')}`
                ).join('\n')
                : '';

            return {
                ...item,
                blockingsqlarray,
                waitingsqlarray
            };
        });
    }, []);

    const tableInstance = useTable(
            {
                columns,
                data: tableData,
                initialState:
                    {
                        pageIndex: initialPageIndex,
                        pageSize: initialPageSize,
                    },
                disableSortRemove: true
            }, useSortBy, usePagination);

    const
        {
            getTableProps,
            getTableBodyProps,
            headerGroups,
            prepareRow,
            page,
            setPageSize,
            pageOptions,
            pageCount,
            canPreviousPage,
            previousPage,
            canNextPage,
            nextPage,
            gotoPage,
            state: { pageIndex, pageSize }
        } = tableInstance;

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    function clearTableFilter() {
        setTableFilter('');
    }


    // The bracket must be present and on the same line as the return.
    return (
        <div className="card">
            <div className="card-header">
                <i className="fal fa-scroll fa-fw" aria-hidden="true"/>
                Transaction Contention
                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                   data-bs-target="#collapse-usage" aria-expanded="false" aria-controls="collapse-usage"/>
            </div>
            <div id="collapse-usage" className="card-body collapse show">
                <div className="row row-cols-1 row-cols-md-2 table-search">
<div className="col col-md-10">
                    <TableCSVExport
                        tableInstance={tableInstance}
                        filename="Transaction Contention"
                        transformData={transformCsvData}
                        headers={headers}
                    />
                    </div>
                    <div className="col col-md-2">
                        <SearchInput
                            ref={inputRef}
                            value={tableFilter}
                            onChange={(e) => setTableFilter(e.target.value)}
                            onClear={clearTableFilter}
                        />
                    </div>
                </div>
                <ReactTable
                    getTableProps={getTableProps}
                    getTableBodyProps={getTableBodyProps}
                    headerGroups={headerGroups}
                    prepareRow={prepareRow}
                    page={page}
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    pageOptions={pageOptions}
                    pageCount={pageCount}
                    gotoPage={gotoPage}
                    nextPage={nextPage}
                    previousPage={previousPage}
                    setPageSize={setPageSize}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    dataLength={data.length}
                    error={error}
                    loading={loading}
                />
            </div>
        </div>
    )
}
